// ============================================================
// libraryModel - the Character Library's props and what its list shows.
//
// Pure: the recent/other split, the flat order a shift-range walks,
// who is picked and how many bodies that makes, and the room a staging
// is reported under. The panels in this folder render from these.
// ============================================================
import type { Character } from '../../engine/character/types';
import type { UseCharacterLibraryReturn } from '../../hooks/useSessionCharacterLibrary';
import type { TableAssets } from '../../hooks/useShellDomainAssets';
import { totalByCount } from '../../utils/expandByCount';

export interface CharacterLibraryProps {
  library: UseCharacterLibraryReturn;
  onEdit: (character: Character) => void;
  onNew: () => void;
  /**
   * Add the character to the roster. The dialog that follows asks how
   * many and which of the caller's private pointers should move - see
   * SpawnDialog. Replaces the old as-attacker / as-target / as-both
   * trio, which described a split the data model no longer has.
   */
  onLoadCharacter?: (character: Character) => void;
  /**
   * Stage them, into a NAMED room when one is chosen. Absent for anyone
   * without the grant.
   *
   * Returns the room's name - the handler may have just MADE the room,
   * so it is the only one that knows - or `null` when nothing happened.
   * Returning nothing is fine; the caller falls back to the chooser.
   */
  onSendToRoom?: (characters: Character[], roomId?: string) => string | null | void;
  /** Put several straight onto the roster, skipping the spawn dialog. */
  onLoadMany?: (characters: Character[]) => void;
  /** The rooms that exist, so staging can name one instead of guessing. */
  waitingRooms?: { id: string; name: string }[];
  /**
   * The chosen folder, held by whoever owns this panel.
   *
   * The panel unmounts whenever it loses focus, so a folder kept inside
   * it had to be chosen again on every visit (v8.24.0).
   */
  folderFiles?: File[] | null;
  onFolderFiles?: (files: File[]) => void;
  isOpen: boolean;
  onClose: () => void;
  /** The GM's asset library at the table, when their account can host (plan assets-library, stage 2). */
  tableAssets?: TableAssets | null;
}

/** The chosen room's name, or the honest fallback for "whichever is active". */
export function roomName(rooms: { id: string; name: string }[], id: string): string {
  return rooms.find((r) => r.id === id)?.name ?? 'the active room';
}

/**
 * The list as drawn: recently loaded characters that still exist (in
 * recent order), then everyone else; `flat` is both, the order a
 * shift-range walks. `picked` is the selection in that order, `total`
 * the bodies it makes once each row's count applies, and `single` the
 * one selected character when exactly one is.
 */
export function libraryLists(
  characters: Character[],
  recentIds: string[],
  selectedIds: Set<string>,
  counts: Record<string, number>,
) {
  const recent = recentIds.map((id) => characters.find((c) => c.id === id)).filter((c): c is Character => c != null);
  const recentIdSet = new Set(recentIds);
  const others = characters.filter((c) => !recentIdSet.has(c.id));
  const flat = [...recent, ...others];
  const picked = flat.filter((c) => selectedIds.has(c.id));
  const single = selectedIds.size === 1 ? (characters.find((c) => selectedIds.has(c.id)) ?? null) : null;
  return { recent, others, flat, picked, total: totalByCount(picked, counts), single };
}

/** A row's count, clamped to 1-99; anything unreadable is 1. */
export function clampCount(raw: string): number {
  return Math.max(1, Math.min(99, Number(raw) || 1));
}
